/**
 * Text Metrics Utility
 * Computes word count, sentence count, Flesch-Kincaid reading grade, and reading time.
 */

export interface TextMetricsResult {
    wordCount: number;
    sentenceCount: number;
    syllableCount: number;
    readingGrade: number;
    readingTime: string;
    gradeLabel: string;
    gradeColor: string;
}

/**
 * Count words in text
 */
export function countWords(text: string): number {
    const cleaned = text.trim();
    if (cleaned.length === 0) return 0;
    return cleaned.split(/\s+/).filter(w => w.length > 0).length;
}

/**
 * Count sentences in text (splits on . ! ?)
 */
export function countSentences(text: string): number {
    const cleaned = text.trim();
    if (cleaned.length === 0) return 0;
    const matches = cleaned.match(/[.!?]+/g);
    return matches ? matches.length : 1;
}

/**
 * Estimate syllable count for a word (English heuristic)
 */
function countWordSyllables(word: string): number {
    const w = word.toLowerCase().replace(/[^a-z]/g, '');
    if (w.length <= 2) return 1;

    let count = 0;
    const vowels = 'aeiouy';
    let prevVowel = false;

    for (let i = 0; i < w.length; i++) {
        const isVowel = vowels.includes(w[i]);
        if (isVowel && !prevVowel) {
            count++;
        }
        prevVowel = isVowel;
    }

    // Silent e
    if (w.endsWith('e') && count > 1) {
        count--;
    }

    // Minimum 1 syllable
    return Math.max(1, count);
}

/**
 * Count total syllables in text
 */
export function countSyllables(text: string): number {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    return words.reduce((sum, word) => sum + countWordSyllables(word), 0);
}

/**
 * Flesch-Kincaid Grade Level
 * Formula: 0.39 × (words/sentences) + 11.8 × (syllables/words) - 15.59
 */
export function fleschKincaidGrade(wordCount: number, sentenceCount: number, syllableCount: number): number {
    if (wordCount === 0 || sentenceCount === 0) return 0;
    const grade = 0.39 * (wordCount / sentenceCount) + 11.8 * (syllableCount / wordCount) - 15.59;
    return Math.max(0, Math.round(grade * 10) / 10);
}

/**
 * Estimated reading time at 200 words per minute
 */
export function readingTime(wordCount: number): string {
    if (wordCount === 0) return '0s';
    const minutes = wordCount / 200;
    if (minutes < 1) return `${Math.ceil(minutes * 60)}s`;
    if (minutes < 2) return '~1 min';
    return `~${Math.round(minutes)} min`;
}

/**
 * Get grade label and color
 */
function gradeInfo(grade: number): { label: string; color: string } {
    if (grade <= 6) return { label: `Grade ${Math.round(grade)} · Easy`, color: 'text-green-600' };
    if (grade <= 12) return { label: `Grade ${Math.round(grade)} · Moderate`, color: 'text-blue-600' };
    return { label: `Grade ${Math.round(grade)} · Advanced`, color: 'text-orange-600' };
}

/**
 * Strip markdown for clean text analysis
 */
function stripMarkdown(text: string): string {
    return text
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
        .replace(/^>\s+/gm, '')
        .replace(/^[\s]*[-*+]\s+/gm, '')
        .replace(/^[\s]*\d+\.\s+/gm, '')
        .replace(/~~(.+?)~~/g, '$1')
        .trim();
}

/**
 * Compute all metrics for a given text
 */
export function getTextMetrics(text: string, isMarkdown = false): TextMetricsResult {
    const cleanText = isMarkdown ? stripMarkdown(text) : text;
    const words = countWords(cleanText);
    const sentences = countSentences(cleanText);
    const syllables = countSyllables(cleanText);
    const grade = fleschKincaidGrade(words, sentences, syllables);
    const { label, color } = gradeInfo(grade);

    return {
        wordCount: words,
        sentenceCount: sentences,
        syllableCount: syllables,
        readingGrade: grade,
        readingTime: readingTime(words),
        gradeLabel: label,
        gradeColor: color,
    };
}
